import axios from "axios";
import { denganKunci } from "./idem";
import { pesanValidasi } from "./pesanValidasi";
import { detailBatas } from "./pesanBatas";

// G3-0: empty = API on the same domain ("/api", production behind Nginx)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const http = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 12000,
});

// v2.21.1: Nginx limit pages (413/429) carry no "detail" — every screen then shows a clear message
http.interceptors.response.use(undefined, (err) => {
  const r = err.response;
  const d = r && detailBatas(r.status, r.data);
  if (d) r.data = { detail: d };
  return Promise.reject(err);
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Terjadi kesalahan. Silakan coba lagi.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return pesanValidasi(detail);  // owner 2026-10-09: field + row, Indonesian
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// --- health ---
export async function fetchHealth() {
  return (await http.get("/health")).data;
}

// --- auth ---
export async function apiLogin(username, password) {
  return (await http.post("/auth/login", { username, password })).data;
}
export async function apiLogout() {
  return (await http.post("/auth/logout")).data;
}
export async function apiMe() {
  return (await http.get("/auth/me")).data;
}

// --- users (admin) ---
export async function apiListUsers() {
  return (await http.get("/users")).data;
}
export async function apiCreateUser(payload) {
  return (await http.post("/users", payload)).data;
}
export async function apiUpdateUser(id, payload) {
  return (await http.put(`/users/${id}`, payload)).data;
}
// T2-B: finished work of my station (default mine, last 31 days)
export async function apiRiwayatPos(params = {}) {
  return (await http.get("/production/riwayat-pos", { params })).data;
}
// v2.24: pieces in / finished per station (today / this month), Atasan / Celana / Kaos; the server picks the stations
export async function apiRekapPcs(params = {}) {
  return (await http.get("/production/rekap-pcs", { params })).data;
}
// T2-A: work an account still holds; move work in progress to another operator (Owner)
export async function apiPekerjaanUser(id) {
  return (await http.get(`/users/${id}/pekerjaan`)).data;
}
export async function apiCalonAlihkan(spkId, instanceId, urutan, bagianId = null) {
  return (await http.get(`/production/queue/${spkId}/steps/${instanceId}/${urutan}/calon`, { params: bagianId ? { bagian_id: bagianId } : {} })).data;
}
// T2-C: tailors' parts (start/finish own part) and the Owner's change of the split
export async function apiStartBagian(spkId, instanceId, urutan, bagianId) {
  return (await http.post(`/production/queue/${spkId}/steps/${instanceId}/${urutan}/bagian/${bagianId}/start`)).data;
}
export async function apiFinishBagian(spkId, instanceId, urutan, bagianId, body) {
  return (await http.post(`/production/queue/${spkId}/steps/${instanceId}/${urutan}/bagian/${bagianId}/finish`, body)).data;
}
export async function apiUbahPembagian(spkId, instanceId, urutan, payload) {
  return (await http.post(`/production/queue/${spkId}/steps/${instanceId}/${urutan}/pembagian`, payload)).data;
}
export async function apiAlihkan(spkId, instanceId, urutan, payload) {
  return (await http.post(`/production/queue/${spkId}/steps/${instanceId}/${urutan}/alihkan`, payload)).data;
}
export async function apiResetPassword(id, password) {
  return (await http.post(`/users/${id}/password`, { password })).data;
}
// v2.14: Sampah akun (requirement 75 butir 3) — the server decides every right
export async function apiListSampahAkun() {
  return (await http.get("/users", { params: { sampah: 1 } })).data;
}
export async function apiHapusAkun(id, alasan) {
  return (await http.post(`/users/${id}/hapus`, { alasan })).data;
}
// v2.21.1 (owner 2026-10-26): open the login locks of one account (Owner; Manager for employee accounts)
export async function apiBukaKunci(id) {
  return (await http.post(`/users/${id}/buka-kunci`)).data;
}
export async function apiPulihkanAkun(id, alasan) {
  return (await http.post(`/users/${id}/pulihkan`, { alasan })).data;
}
export async function apiHapusPermanenAkun(id, alasan) {
  return (await http.post(`/users/${id}/hapus-permanen`, { alasan })).data;
}

// --- tarif ---
export async function apiListTarif() {
  return (await http.get("/tarif")).data;
}
export async function apiCreateTarif(payload) {
  return (await http.post("/tarif", payload)).data;
}
export async function apiUpdateTarif(id, payload) {
  return (await http.put(`/tarif/${id}`, payload)).data;
}

// --- pos produksi ---
export async function apiListPos() {
  return (await http.get("/pos-produksi")).data;
}
export async function apiCreatePos(payload) {
  return (await http.post("/pos-produksi", payload)).data;
}
export async function apiUpdatePos(id, payload) {
  return (await http.put(`/pos-produksi/${id}`, payload)).data;
}

// --- workflow ---
export async function apiListWorkflows() {
  return (await http.get("/workflows")).data;
}
export async function apiGetWorkflow(id) {
  return (await http.get(`/workflows/${id}`)).data;
}
export async function apiCreateWorkflow(payload) {
  return (await http.post("/workflows", payload)).data;
}
export async function apiUpdateWorkflow(id, payload) {
  return (await http.put(`/workflows/${id}`, payload)).data;
}

// --- customers ---
export async function apiListCustomers() {
  return (await http.get("/customers")).data;
}
export async function apiCreateCustomer(payload) {
  return (await http.post("/customers", payload)).data;
}
export async function apiUpdateCustomer(id, payload) {
  return (await http.put(`/customers/${id}`, payload)).data;
}

// --- status order (no money fields; CS read-only) ---
export async function apiListStatusOrder() {
  return (await http.get("/status-order")).data;
}

// --- transaksi digital print / pengaturan / jalur produksi (F1-B) ---
export async function apiListDesigners() {
  return (await http.get("/designers")).data;
}
// v2.21: the Owner's default designer per door ({JERSEY, DTF}: {id, nama} or null = every designer)
export async function apiDesainerBawaan() {
  return (await http.get("/designers/bawaan")).data;
}
export async function apiPreviewTransaksiDP(payload) {
  return (await http.post("/transaksi/digital-print/preview", payload)).data;
}
export async function apiCreateTransaksiDP(payload, kunci = null) {
  return (await http.post("/transaksi/digital-print", payload, denganKunci(kunci))).data;
}
export async function apiGetTransaksi(orderId) {
  return (await http.get(`/transaksi/${orderId}`)).data;
}
export async function apiGetPengaturan() {
  return (await http.get("/pengaturan")).data;
}
export async function apiUpdatePengaturan(payload) {
  return (await http.put("/pengaturan", payload)).data;
}
export async function apiListJalur() {
  return (await http.get("/jalur-produksi")).data;
}
export async function apiCreateJalur(payload) {
  return (await http.post("/jalur-produksi", payload)).data;
}
export async function apiUpdateJalur(id, payload) {
  return (await http.put(`/jalur-produksi/${id}`, payload)).data;
}

// --- owner 2026-10-15 (A9): Riwayat Pembayaran (Finance & Owner, read-only) ---
// Owner 2026-10-16 (requirement 61): layar produksi settings
export async function apiTvwAtur() { return (await http.get("/layar-produksi/atur")).data; }
export async function apiTvwTeks(teks) { return (await http.put("/layar-produksi/teks", { teks })).data; }
export async function apiTvwKodeBaru() { return (await http.post("/layar-produksi/kode")).data; }
export async function apiTvwKodeCabut() { return (await http.delete("/layar-produksi/kode")).data; }
// v2.15: devices one by one + who opened the screen (Owner)
export async function apiTvwPerangkat() { return (await http.get("/layar-produksi/perangkat")).data; }
export async function apiTvwPerangkatCabut(id) { return (await http.delete(`/layar-produksi/perangkat/${id}`)).data; }
export async function apiTvwAkses(hari = 30) { return (await http.get("/layar-produksi/akses", { params: { hari } })).data; }
// v2.10 (owner 2026-10-19, requirement 70): voice (Owner/Finance) and manual announcements (Owner/Finance/Manager)
export async function apiTvwSuara(body) { return (await http.put("/layar-produksi/suara", body)).data; }
export async function apiTvwContoh(jenis, template) { return (await http.post("/layar-produksi/suara/contoh", { jenis, template })).data; }
export async function apiTvwAlasanReject(alasan) { return (await http.put("/layar-produksi/alasan-reject", { alasan })).data; }  // v2.17 Owner
export async function apiTvwPengumuman(body) { return (await http.post("/layar-produksi/pengumuman", body)).data; }
export async function apiTvwPengumumanBatal(id) { return (await http.post(`/layar-produksi/pengumuman/${id}/batal`)).data; }

// Owner 2026-10-16 (requirement 60): customer ratings, Owner and Manager
export async function apiDaftarUlasan(params) {
  return (await http.get("/ulasan", { params })).data;
}
export async function apiRiwayatPembayaran(params) {
  return (await http.get("/riwayat-pembayaran", { params })).data;
}
export async function apiRiwayatFaktur(notaId) {
  return (await http.get(`/riwayat-pembayaran/faktur/${notaId}`)).data;
}
// --- owner 2026-10-15 (A8): payment reminders (Kasir/CS & Owner) ---
export async function apiPengingatTagihan() {
  return (await http.get("/pengingat-tagihan")).data;
}
export async function apiKirimPengingat(notaId) {
  return (await http.post(`/pengingat-tagihan/${notaId}/kirim`)).data;
}
// --- owner 2026-10-15 (A4): new customers per tier (Owner) ---
export async function apiCustomerBaru(params) {
  return (await http.get("/customers/baru", { params })).data;
}

// --- pembayaran (F1-C) ---
export async function apiListTagihan(status, q) {
  return (await http.get("/pembayaran/tagihan", { params: { status, ...(q ? { q } : {}) } })).data;
}
export async function apiGetPembayaran(notaId) {
  return (await http.get(`/notas/${notaId}/pembayaran`)).data;
}
export async function apiBayar(notaId, payload, kunci = null) {
  return (await http.post(`/notas/${notaId}/pembayaran`, payload, denganKunci(kunci))).data;
}
export async function apiKoreksiPembayaran(pembayaranId, payload) {
  return (await http.post(`/pembayaran/${pembayaranId}/koreksi`, payload)).data;
}
export async function apiGetCetakNota(notaId, pembayaranId) {
  return (await http.get(`/notas/${notaId}/cetak`, { params: pembayaranId ? { pembayaran_id: pembayaranId } : {} })).data;
}

// --- institution order without DP (Addendum 5, Owner) ---
export async function apiTanpaDp(orderId, alasan) {
  return (await http.post(`/transaksi/${orderId}/tanpa-dp`, { alasan })).data;
}

// --- invoice A4 + termin (Addendum 5) ---
export async function apiGetInvoice(notaId, params) {
  return (await http.get(`/notas/${notaId}/invoice`, { params })).data;
}
export async function apiCetakInvoice(notaId, payload) {
  return (await http.post(`/notas/${notaId}/invoice`, payload)).data;
}
export async function apiRiwayatInvoice(notaId) {
  return (await http.get(`/notas/${notaId}/invoice/riwayat`)).data;
}

// --- pembatalan nota & pengembalian (F1-D, Owner) ---
export async function apiBatalPratinjau(notaId) {
  return (await http.get(`/notas/${notaId}/batal/pratinjau`)).data;
}
export async function apiBatalNota(notaId, alasan) {
  return (await http.post(`/notas/${notaId}/batal`, { alasan })).data;
}
export async function apiTindakLanjut() {
  return (await http.get("/pembayaran/tindak-lanjut")).data;
}
export async function apiGetPengembalian(notaId) {
  return (await http.get(`/notas/${notaId}/pengembalian`)).data;
}
export async function apiCatatPengembalian(notaId, payload) {
  return (await http.post(`/notas/${notaId}/pengembalian`, payload)).data;
}
export async function apiKoreksiPengembalian(entriId, payload) {
  return (await http.post(`/pengembalian/${entriId}/koreksi`, payload)).data;
}

// --- buku kas (F1-G) ---
export async function apiListKasKategori(aktif) {
  return (await http.get("/kas-kategori", { params: aktif === undefined ? {} : { aktif } })).data;
}
export async function apiCreateKasKategori(payload) {
  return (await http.post("/kas-kategori", payload)).data;
}
export async function apiUpdateKasKategori(id, payload) {
  return (await http.put(`/kas-kategori/${id}`, payload)).data;
}
export async function apiBukuKas(params) {
  return (await http.get("/kas", { params })).data;
}
export async function apiCatatKas(payload) {
  return (await http.post("/kas", payload)).data;
}
export async function apiKoreksiKas(id, payload) {
  return (await http.post(`/kas/${id}/koreksi`, payload)).data;
}
// owner 2026-10-17: transfer between cash accounts (TRANSFER-KAS)
export async function apiListTransferKas(params) {
  return (await http.get("/kas/transfer", { params })).data;
}
export async function apiPratinjauTransferKas(payload) {
  return (await http.post("/kas/transfer/pratinjau", payload)).data;
}
export async function apiTransferKas(payload, kunci = null) {
  return (await http.post("/kas/transfer", payload, denganKunci(kunci))).data;
}
export async function apiBatalTransferKas(id, alasan) {
  return (await http.post(`/kas/transfer/${id}/batal`, { alasan })).data;
}

// --- master barang (F1-A) ---
export async function apiListMaster(path) {
  return (await http.get(`/${path}`)).data; // path: kategori-barang | jenis-mesin
}
export async function apiCreateMaster(path, payload) {
  return (await http.post(`/${path}`, payload)).data;
}
export async function apiUpdateMaster(path, id, payload) {
  return (await http.put(`/${path}/${id}`, payload)).data;
}
export async function apiListBarang(params = {}) {
  return (await http.get("/barang", { params })).data;
}
export async function apiCreateBarang(payload) {
  return (await http.post("/barang", payload)).data;
}
export async function apiUpdateBarang(id, payload) {
  return (await http.put(`/barang/${id}`, payload)).data;
}
export async function apiReplaceStrata(id, strata) {
  return (await http.put(`/barang/${id}/strata`, { strata })).data;
}
// Price List (owner 2026-10-10): Enduser prices only, read-only
export async function apiPriceList() {
  return (await http.get("/price-list")).data;
}
export async function apiGetHarga(id, tipe_customer, qty) {
  return (await http.get(`/barang/${id}/harga`, { params: { tipe_customer, qty } })).data;
}
export async function apiListBahan(params = {}) {
  return (await http.get("/bahan", { params })).data;
}
export async function apiCreateBahan(payload) {
  return (await http.post("/bahan", payload)).data;
}
export async function apiUpdateBahan(id, payload) {
  return (await http.put(`/bahan/${id}`, payload)).data;
}
export async function apiListSupplier(params = {}) {
  return (await http.get("/supplier", { params })).data;
}
export async function apiCreateSupplier(payload) {
  return (await http.post("/supplier", payload)).data;
}
export async function apiUpdateSupplier(id, payload) {
  return (await http.put(`/supplier/${id}`, payload)).data;
}

// --- orders ---
export async function apiListOrders() {
  return (await http.get("/orders")).data;
}
export async function apiGetOrder(id) {
  return (await http.get(`/orders/${id}`)).data;
}
export async function apiCreateOrder(payload) {
  return (await http.post("/orders", payload)).data;
}
export async function apiUpdateOrder(id, payload) {
  return (await http.put(`/orders/${id}`, payload)).data;
}
export async function apiChangeOrderStatus(id, status_order) {
  return (await http.post(`/orders/${id}/status`, { status_order })).data;
}

// --- spk ---
export async function apiListSpks() {
  return (await http.get("/spks")).data;
}
export async function apiGetSpk(id) {
  return (await http.get(`/spks/${id}`)).data;
}
// owner 2026-10-12: the Manager requests an SPK cancellation; the owner decides
export async function apiAjukanBatalSpk(id, alasan) {
  return (await http.post(`/spks/${id}/pengajuan-batal`, { alasan })).data;
}
export async function apiDaftarBatalSpk(status) {
  return (await http.get("/spk-batal", { params: status ? { status } : {} })).data;
}
export async function apiPutuskanBatalSpk(id, keputusan, catatan) {
  return (await http.post(`/spk-batal/${id}/putuskan`, { keputusan, catatan })).data;
}
export async function apiCreateSpkFromOrder(orderId) {
  return (await http.post(`/orders/${orderId}/spk`)).data;
}
export async function apiSetSpkReady(id) {
  return (await http.post(`/spks/${id}/ready`)).data;
}

// --- nota ---
export async function apiListNotas() {
  return (await http.get("/notas")).data;
}
export async function apiGetNota(id) {
  return (await http.get(`/notas/${id}`)).data;
}
export async function apiCreateNotaFromSpk(spkId) {
  return (await http.post(`/spks/${spkId}/nota`)).data;
}
export async function apiChangeNotaStatus(id, status_nota) {
  return (await http.post(`/notas/${id}/status`, { status_nota })).data;
}

// --- production queue (read-only) ---
export async function apiQueueTampilan() { return (await http.get("/production/queue-tampilan")).data; }  // v2.16-A
export async function apiGetProductionQueue(params = {}) {
  return (await http.get("/production/queue", { params })).data;
}export async function apiGetProductionQueueDetail(spkId) {
  return (await http.get(`/production/queue/${spkId}`)).data;
}
export async function apiStartStep(spkId, instanceId, urutan) {
  return (await http.post(`/production/queue/${spkId}/steps/${instanceId}/${urutan}/start`)).data;
}
export async function apiFinishStep(spkId, instanceId, urutan, payload) {
  return (await http.post(`/production/queue/${spkId}/steps/${instanceId}/${urutan}/finish`, payload)).data;
}
export async function apiQcStep(spkId, instanceId, urutan, payload) {
  return (await http.post(`/production/queue/${spkId}/steps/${instanceId}/${urutan}/qc`, payload)).data;
}
// v2.17 (owner 2026-10-22, requirement 75 butir 9d): a station reports a problem to an earlier one
export async function apiLaporMasalah(spkId, instanceId, urutan, payload) {
  return (await http.post(`/production/queue/${spkId}/steps/${instanceId}/${urutan}/lapor-masalah`, payload)).data;
}
export async function apiAlasanReject() { return (await http.get("/production/alasan-reject")).data; }

// --- dashboard + monitoring (admin, read-only) ---
export async function apiGetDashboardSummary() {
  return (await http.get("/dashboard/summary")).data;
}
export async function apiGetMonitoring(params = {}) {
  return (await http.get("/dashboard/monitoring", { params })).data;
}

// --- stok, BOM, pembelian & utang supplier (F1-E) ---
export async function apiListBom(barangId) {
  return (await http.get("/bom", { params: { barang_id: barangId } })).data;
}
export async function apiCreateBom(payload) {
  return (await http.post("/bom", payload)).data;
}
export async function apiUpdateBom(id, payload) {
  return (await http.put(`/bom/${id}`, payload)).data;
}
export async function apiListStok(params = {}) {
  return (await http.get("/stok", { params })).data;
}
export async function apiKartuStok(bahanId, params = {}) {
  return (await http.get(`/stok/${bahanId}/kartu`, { params })).data;
}
export async function apiPenyesuaianStok(bahanId, payload) {
  return (await http.post(`/stok/${bahanId}/penyesuaian`, payload)).data;
}
export async function apiStokMinimum(bahanId, stok_minimum) {
  return (await http.put(`/stok/${bahanId}/minimum`, { stok_minimum })).data;
}
export async function apiKoreksiMutasi(mutasiId, payload) {
  return (await http.post(`/stok/mutasi/${mutasiId}/koreksi`, payload)).data;
}
export async function apiStokPeringatan() {
  return (await http.get("/stok/peringatan")).data;
}
export async function apiStokTindakLanjut() {
  return (await http.get("/stok/tindak-lanjut")).data;
}
export async function apiStokKeputusan(notaId, payload) {
  return (await http.post(`/notas/${notaId}/stok-keputusan`, payload)).data;
}
export async function apiListPembelian(params = {}) {
  return (await http.get("/pembelian", { params })).data;
}
export async function apiCreatePembelian(payload, kunci = null) {
  return (await http.post("/pembelian", payload, denganKunci(kunci))).data;
}
export async function apiBatalPembelian(id, alasan, uang) {
  return (await http.post(`/pembelian/${id}/batal`, uang ? { alasan, uang } : { alasan })).data;
}
// owner 2026-10-17: a paid purchase cancelled as a wrong entry — money back to the paying accounts
export async function apiRingkasanKembaliPembelian(id) {
  return (await http.get(`/pembelian/${id}/kembalikan-uang`)).data;
}
export async function apiKembalikanUangPembelian(id, alasan) {
  return (await http.post(`/pembelian/${id}/kembalikan-uang`, { alasan })).data;
}
export async function apiRiwayatBayarPembelian(id) {
  return (await http.get(`/pembelian/${id}/pembayaran`)).data;
}
export async function apiBayarUtang(id, payload, kunci = null) {
  return (await http.post(`/pembelian/${id}/pembayaran`, payload, denganKunci(kunci))).data;
}
export async function apiKoreksiBayarUtang(pembayaranId, payload) {
  return (await http.post(`/pembayaran-supplier/${pembayaranId}/koreksi`, payload)).data;
}
export async function apiUtangSupplier(params = {}) {
  return (await http.get("/utang-supplier", { params })).data;
}

// --- order jersey (F1-F) ---
export async function apiPreviewJersey(payload) {
  return (await http.post("/transaksi/jersey/pratinjau", payload)).data;
}
export async function apiCreateJersey(payload, kunci = null) {
  return (await http.post("/transaksi/jersey", payload, denganKunci(kunci))).data;
}
export async function apiGetJersey(orderId) {
  return (await http.get(`/transaksi/jersey/${orderId}`)).data;
}
export async function apiPreviewUbahJersey(orderId, payload) {
  return (await http.post(`/transaksi/jersey/${orderId}/ubah/pratinjau`, payload)).data;
}
export async function apiUbahJersey(orderId, payload, versi) {
  return (await http.put(`/transaksi/jersey/${orderId}`, payload, { params: { versi } })).data;
}
export async function apiJerseyChecklist(orderId, kunci, ok) {
  return (await http.post(`/transaksi/jersey/${orderId}/checklist`, { kunci, ok })).data;
}
export async function apiJerseyChecklistSemua(orderId) {
  return (await http.post(`/transaksi/jersey/${orderId}/checklist/semua`)).data;
}
export async function apiJerseyDuplikat(orderId, alasan) {
  return (await http.post(`/transaksi/jersey/${orderId}/duplikat-disengaja`, { alasan })).data;
}
// v2.23: names & numbers after the customer's ACC (sizes must stay; no revision, no ACC reset)
export async function apiJerseyLengkapiPemain(orderId, pemain, versi) {
  return (await http.post(`/transaksi/jersey/${orderId}/pemain`, { pemain }, { params: versi != null ? { versi } : {} })).data;
}
export async function apiJerseySiapAcc(orderId) {
  return (await http.post(`/transaksi/jersey/${orderId}/siap-acc`)).data;
}
export async function apiJerseyAcc(orderId) {
  return (await http.post(`/transaksi/jersey/${orderId}/acc`)).data;
}
export async function apiJerseyBukaKunci(orderId, alasan) {
  return (await http.post(`/transaksi/jersey/${orderId}/buka-kunci`, { alasan })).data;
}
export async function apiJerseyOrderMasuk(status) {
  return (await http.get("/jersey/order-masuk", { params: { status } })).data;
}
export async function apiJerseySiapDesain() {
  return (await http.get("/jersey/siap-desain")).data;
}
export async function apiJerseyQc(spkId, indeks, ok) {
  return (await http.post(`/production/queue/${spkId}/jersey-qc`, { indeks, ok })).data;
}

// --- siap diambil & laporan (F1-H) ---
export async function apiSiapDiambil(jenis, q) {
  return (await http.get("/penyerahan/siap", { params: { jenis, q: q || undefined } })).data;
}
export async function apiRiwayatPenyerahan(jenis, params) {
  return (await http.get("/penyerahan/riwayat", { params: { jenis, ...params } })).data;
}
export async function apiSerahkan(orderId, payload) {
  return (await http.post(`/penyerahan/${orderId}`, payload)).data;
}
export async function apiBatalPenyerahan(orderId, alasan) {
  return (await http.post(`/penyerahan/${orderId}/batal`, { alasan })).data;
}
export async function apiLaporan(nama, params) {
  const clean = Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v));
  return (await http.get(`/laporan/${nama}`, { params: clean })).data;
}

// --- import master barang dari Excel (F1.5-C, Owner) ---
export async function apiImportBarangDryRun(file) {
  const fd = new FormData();
  fd.append("file", file);
  return (await http.post("/barang/import/dry-run", fd, { timeout: 60000 })).data;
}
export async function apiImportBarangApply(token) {
  return (await http.post("/barang/import/apply", { token }, { timeout: 60000 })).data;
}
// owner 2026-10-12: download for Excel (Owner + Manager; Manager's file without HPP), history & undo (Owner)
export async function apiUnduhBarang() {
  const r = await http.get("/barang/export.xlsx", { responseType: "blob", timeout: 60000 });
  const nama = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "")?.[1] || "barang.xlsx";
  return { blob: r.data, nama };
}
// owner 2026-10-17: Excel template of the Jersey name list (columns and choices = the form)
export async function apiUnduhTemplatePemainJersey() {
  const r = await http.get("/transaksi/jersey/template-pemain.xlsx", { responseType: "blob", timeout: 60000 });
  const nama = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "")?.[1] || "template-nama-nomor-jersey.xlsx";
  return { blob: r.data, nama };
}
export async function apiImportBarangRiwayat() {
  return (await http.get("/barang/import/riwayat")).data;
}
export async function apiImportBarangKembalikan(id, alasan) {
  return (await http.post(`/barang/import/${id}/kembalikan`, { alasan }, { timeout: 60000 })).data;
}

// --- v2.11 (owner 2026-10-19, requirement 71): thermal label queue (QC, Owner) ---
export async function apiLabelAntrean(status = "ANTRE") { return (await http.get("/label/antrean", { params: { status } })).data; }
export async function apiLabelCetak(id) { return (await http.post(`/label/${id}/cetak`)).data; }
export async function apiLabelContoh() { return (await http.get("/label/contoh")).data; }
// --- v2.11: opening stock + BOM import (Owner & Finance; undo Owner) ---
export async function apiStokImporTemplate() {
  const r = await http.get("/stok/impor/template.xlsx", { responseType: "blob", timeout: 60000 });
  const nama = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "")?.[1] || "template-stok-awal-bom.xlsx";
  return { blob: r.data, nama };
}
export async function apiStokImporDryRun(file) {
  const fd = new FormData();
  fd.append("file", file);
  return (await http.post("/stok/impor/dry-run", fd, { timeout: 60000 })).data;
}
export async function apiStokImporApply(token) { return (await http.post("/stok/impor/apply", { token }, { timeout: 120000 })).data; }
export async function apiStokImporRiwayat() { return (await http.get("/stok/impor/riwayat")).data; }
export async function apiStokImporKembalikan(id, alasan) { return (await http.post(`/stok/impor/${id}/kembalikan`, { alasan }, { timeout: 120000 })).data; }

// --- kelebihan bayar (F1.5-E, Owner & Finance) ---
export async function apiListKelebihan(status) {
  return (await http.get("/kelebihan-bayar", { params: status ? { status } : {} })).data;
}
export async function apiKembalikanKelebihan(id, payload) {
  return (await http.post(`/kelebihan-bayar/${id}/pengembalian`, payload)).data;
}
export async function apiKoreksiKelebihan(id, payload) {
  return (await http.post(`/kelebihan-bayar/${id}/koreksi`, payload)).data;
}

// --- stok rework (owner 2026-10-08) ---
export async function apiStokRework(params = {}) {
  return (await http.get("/stok/rework", { params })).data;
}
export async function apiStokReworkUlangi(spkId, reworkKe) {
  return (await http.post(`/stok/rework/${spkId}/${reworkKe}/ulangi`)).data;
}

// --- antrean stok (F1.5-F) ---
export async function apiStokMenunggu() {
  return (await http.get("/stok/menunggu")).data;
}
export async function apiProsesAntreanStok() {
  return (await http.post("/stok/antrean/proses")).data;
}
export async function apiLepasPaksa(spkId, alasan) {
  return (await http.post(`/spks/${spkId}/lepas-paksa`, { alasan })).data;
}

// --- DTF, Custom, rencana selesai (F1.5-G) ---
export async function apiPreviewTransaksi(jenis, payload) {
  const path = { DIGITAL_PRINT: "digital-print", DTF: "dtf", CUSTOM: "custom" }[jenis];
  return (await http.post(`/transaksi/${path}/preview`, payload)).data;
}
export async function apiCreateTransaksi(jenis, payload, kunci = null) {
  const path = { DIGITAL_PRINT: "digital-print", DTF: "dtf", CUSTOM: "custom" }[jenis];
  return (await http.post(`/transaksi/${path}`, payload, denganKunci(kunci))).data;
}
export async function apiDtfMenungguAcc() {
  return (await http.get("/transaksi/dtf/menunggu-acc")).data;
}
export async function apiUbahRencana(orderId, tanggal, alasan, jam = null) {
  return (await http.put(`/transaksi/${orderId}/rencana-selesai`, { tanggal, alasan, jam })).data;
}

// --- mockup & ACC customer (F1.5-I) ---
export const mockupUrl = (orderId, versi) => `${API}/transaksi/${orderId}/mockup/${versi}`;
// owner 2026-10-07: the mockup in the production queue (every station of the SPK)
// owner 2026-10-12: SPK Jersey as a worksheet + Excel A4 (server decides who sees players' names)
export async function apiLembarKerja(spkId) {
  return (await http.get(`/spks/${spkId}/lembar-kerja`)).data;
}
// owner 2026-10-15 (A5): one labelled picture of the set by its version
export const lembarKerjaMockupUrl = (spkId, versi) => `${API}/spks/${spkId}/lembar-kerja/mockup${versi ? `?versi=${versi}` : ""}`;
export async function apiUnduhLembarKerja(spkId) {
  const r = await http.get(`/spks/${spkId}/lembar-kerja.xlsx`, { responseType: "blob", timeout: 60000 });
  const nama = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "")?.[1] || "lembar-kerja.xlsx";
  return { blob: r.data, nama };
}
export const antreanMockupUrl = (spkId, versi) => `${API}/production/queue/${spkId}/mockup/${versi}`;
export async function apiUploadMockup(orderId, file, slot = "PENUH", desainKe = 1) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("slot", slot);  // owner 2026-10-15 (A5): Jersey labelled slots
  fd.append("desain_ke", String(desainKe || 1));  // v2.21: the design of a split Jersey order
  return (await http.post(`/transaksi/${orderId}/mockup`, fd, { timeout: 60000 })).data;
}
export async function apiListMockup(orderId) {
  return (await http.get(`/transaksi/${orderId}/mockup`)).data;
}
export async function apiAccLink(orderId, dikirim) {
  // v2.19: dikirim = "SALIN" when the CS copies the link to send it (a finished revision leaves its tab)
  return (await http.post(`/transaksi/${orderId}/acc-link`, null, dikirim ? { params: { dikirim } } : undefined)).data;
}
export async function apiRevisiSelesai(orderId) {  // v2.19: "Revisi selesai, kirim ke CS"
  return (await http.post(`/transaksi/${orderId}/mockup/revisi-selesai`)).data;
}
export async function apiAccLinkCabut(orderId) {
  return (await http.post(`/transaksi/${orderId}/acc-link/cabut`)).data;
}
export async function apiAccWhatsapp(orderId, payload) {
  return (await http.post(`/transaksi/${orderId}/acc-customer`, payload)).data;
}
// v2.18 C: reference pictures for the designer (never on SPK / lembar kerja / ACC page / nota)
export const referensiUrl = (orderId, refId) => `${API}/transaksi/${orderId}/referensi/${refId}/gambar`;
export async function apiReferensi(orderId) {
  return (await http.get(`/transaksi/${orderId}/referensi`)).data;
}
export async function apiUploadReferensi(orderId, file) {
  const fd = new FormData();
  fd.append("file", file);
  return (await http.post(`/transaksi/${orderId}/referensi`, fd, { timeout: 60000 })).data;
}
export async function apiHapusReferensi(orderId, refId) {
  return (await http.delete(`/transaksi/${orderId}/referensi/${refId}`)).data;
}
// v2.18 D: Owner / Manager approve an ACC revision past the limit
export async function apiIzinRevisi(orderId, alasan) {
  return (await http.post(`/transaksi/${orderId}/revisi-izin`, { alasan })).data;
}
// v2.19: the ACC & Mockup page — every row with its tab, SPK, CS and since-when
export async function apiAccRingkas() {
  return (await http.get("/transaksi/acc/ringkas")).data;
}
export async function apiAccDaftar(jenis) {
  return (await http.get("/transaksi/acc/daftar", { params: jenis ? { jenis } : {} })).data;
}
// F1.5-J: station forms, production history
export async function apiFormPos() {
  return (await http.get("/form-pos")).data;
}
export async function apiUbahFormPos(pos, payload) {
  return (await http.put(`/form-pos/${pos}`, payload)).data;
}
export async function apiRiwayatProduksi(params) {
  return (await http.get("/riwayat-produksi", { params })).data;
}
export async function apiRiwayatProduksiDetail(spkId) {
  return (await http.get(`/riwayat-produksi/${spkId}`)).data;
}
export async function apiSelisihQty() {
  return (await http.get("/production/selisih-qty")).data;
}
// F1.5-K: weekly piece-rate pay
export async function apiBoronganTarif() {
  return (await http.get("/borongan/tarif")).data;
}
export async function apiBoronganTarifBuat(payload) {
  return (await http.post("/borongan/tarif", payload)).data;
}
export async function apiBoronganTarifNonaktif(id, alasan) {
  return (await http.post(`/borongan/tarif/${id}/nonaktif`, { alasan })).data;
}
export async function apiBoronganRekap(minggu) {
  return (await http.get("/borongan/rekap", { params: minggu ? { minggu } : {} })).data;
}
export async function apiBoronganEntri(minggu, operatorId) {
  return (await http.get("/borongan/entri", { params: { minggu, ...(operatorId ? { operator_id: operatorId } : {}) } })).data;
}
export async function apiBoronganSaya(minggu) {
  return (await http.get("/borongan/saya", { params: minggu ? { minggu } : {} })).data;
}
export async function apiBoronganPenyesuaian(payload) {
  return (await http.post("/borongan/penyesuaian", payload)).data;
}
export async function apiBoronganHitungUlang(minggu) {
  return (await http.post("/borongan/hitung-ulang", null, { params: { minggu } })).data;
}
export async function apiBoronganKunci(minggu) {
  return (await http.post(`/borongan/periode/${minggu}/kunci`)).data;
}
export async function apiBoronganBayar(minggu, payload) {
  return (await http.post(`/borongan/periode/${minggu}/bayar`, payload)).data;
}
// v2.24.3: mark an unpriced HASIL entry as settled by a Penyesuaian (owner, reason)
export async function apiBoronganSelesaikan(entriId, alasan) {
  return (await http.post(`/borongan/entri/${entriId}/selesaikan`, { alasan })).data;
}
export async function apiBoronganCek(perbaiki) {
  return (perbaiki ? await http.post("/borongan/cek/perbaiki") : await http.get("/borongan/cek")).data;
}
// F1.5-L: WhatsApp outbox
export async function apiWaStatus() {
  return (await http.get("/wa/status")).data;
}
export async function apiWaPesan(params) {
  return (await http.get("/wa/pesan", { params })).data;
}
export async function apiWaOrder(orderId) {
  return (await http.get(`/wa/order/${orderId}`)).data;
}
export async function apiWaUlang(id) {
  return (await http.post(`/wa/pesan/${id}/ulang`)).data;
}
export async function apiWaProses() {
  return (await http.post("/wa/proses")).data;
}
export async function apiAccKirim(orderId) {
  return (await http.post(`/transaksi/${orderId}/acc-link/kirim`)).data;
}

// --- logo (G1-B, Owner) ---
export async function apiUploadLogo(varian, file) {
  const fd = new FormData();
  fd.append("file", file);
  return (await http.post(`/pengaturan/logo/${varian}`, fd, { timeout: 60000 })).data;
}
export async function apiResetLogo(varian) {
  return (await http.delete(`/pengaturan/logo/${varian}`)).data;
}

// --- kirim link faktur (G1-E) ---
export async function apiKirimLinkNota(notaId) {
  return (await http.get(`/notas/${notaId}/kirim`)).data;
}

// --- notifikasi owner (G1-D) ---
// v2.19: the Kasir/CS and the Manager have their own bell ("/notifikasi/saya": only what is addressed to them)
const notifDasar = (saya) => (saya ? "/notifikasi/saya" : "/notifikasi");
export const apiNotifikasi = async (saya) => (await http.get(notifDasar(saya))).data;
export const apiBacaNotifikasi = async (id, saya) => (await http.post(`${notifDasar(saya)}/${id}/baca`)).data;
export const apiBacaSemuaNotifikasi = async (saya) => (await http.post(`${notifDasar(saya)}/baca-semua`)).data;
export const apiPushKunci = async () => (await http.get("/notifikasi/push/kunci")).data;
export const apiPushLangganan = async (sub) => (await http.post("/notifikasi/push/langganan", sub)).data;
export const apiPushBerhenti = async (endpoint) => (await http.post("/notifikasi/push/berhenti", { endpoint })).data;
export const apiPushUji = async () => (await http.post("/notifikasi/push/uji")).data;

// --- TARIF-JAHIT: sewing types and their rates (owner writes; rupiah only for owner & finance) ---
export async function apiJenisJahit(kelompok) {
  return (await http.get("/jenis-jahit", { params: kelompok ? { kelompok } : {} })).data;
}
export async function apiBuatJenisJahit(payload) {
  return (await http.post("/jenis-jahit", payload)).data;
}
export async function apiUbahJenisJahit(kode, payload) {
  return (await http.put(`/jenis-jahit/${kode}`, payload)).data;
}
export async function apiTarifJenisJahit(kode, payload) {
  return (await http.post(`/jenis-jahit/${kode}/tarif`, payload)).data;
}
// owner 2026-10-11/12: the owner's dashboard (owner only, checked by the server)
export async function apiDashboardOwner() {
  return (await http.get("/dashboard/owner")).data;
}

// v2.22 file master desain (Cloudflare R2): the server keeps the list and signs short links; the file itself
// goes straight between the browser and R2 (lib/unggahFileMaster.js)
export async function apiFileMaster(spkId) {
  return (await http.get(`/spks/${spkId}/file-master`)).data;
}
export const apiFileMasterUnggah = {
  mulai: async (spkId, body) => (await http.post(`/spks/${spkId}/file-master`, body)).data,
  tautan: async (fid, nomor) => (await http.post(`/file-master/${fid}/tautan-bagian`, { nomor })).data,
  bagian: async (fid) => (await http.get(`/file-master/${fid}/bagian`, { timeout: 30000 })).data,
  selesai: async (fid, bagian) => (await http.post(`/file-master/${fid}/selesai`, { bagian }, { timeout: 120000 })).data,
};
export async function apiFileMasterBatal(fid) {
  return (await http.post(`/file-master/${fid}/batal`)).data;
}
export async function apiFileMasterFinal(fid, catatan) {
  return (await http.post(`/file-master/${fid}/final`, { catatan: catatan || null }, { timeout: 120000 })).data;
}
export async function apiFileMasterBatalFinal(fid, alasan) {
  return (await http.post(`/file-master/${fid}/batal-final`, { alasan }, { timeout: 120000 })).data;
}
export async function apiFileMasterHapus(fid, alasan) {
  return (await http.post(`/file-master/${fid}/hapus`, { alasan })).data;
}
export async function apiFileMasterUnduh(fid) {
  return (await http.post(`/file-master/${fid}/unduh`)).data;
}
export async function apiFileMasterRiwayat(fid) {
  return (await http.get(`/file-master/${fid}/riwayat`)).data;
}
export async function apiFileMasterRingkasan() {
  return (await http.get("/file-master-ringkasan")).data;
}
